import { FC, useState } from 'react';
import moment from 'moment';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import useClinicSchedule from 'hooks/useClinicSchedule';
import { IPractitioner } from 'interfaces/practitioners';
import { NEXT_23_MONTH } from 'utils/constants';
import MonthViewNavigation from 'components/MonthViewNavigation/MonthViewNavigation';
import Calendar from './Calendar/Calendar';
import usePractitionerException from 'hooks/usePractitionerException';
import useClinicException from 'hooks/useClinicException';
import useOutsidePractitionerHour from 'hooks/useOutsidePractitionerHour';
import OutsideOfficeHoursModal from 'pages/OfficeSchedulePage/OutsideOfficeHoursModal/OutsideOfficeHoursModal';
import { IAppointmentDetail } from 'interfaces/appointments';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import OutsideApptIndicator from 'components/OutsideHourIndicator/OutsideHourIndicator';
import useScheduleNavigator from 'hooks/useScheduleNavigator';
import CommonButton from 'components/CommonButton/CommonButton';
import PractitionerSchedule from './PractitionerSchedule/PractitionerSchedule';
import { useSidebar } from 'context/sidebarContext';
import { usePractitionerSidebar } from 'pages/PractitionerPage/context/practitionerSidebarContext';
import usePractitionerSettings from 'hooks/usePractitionerSettings';

interface PractitionerHoursProps {
  selectedPractitioner: IPractitioner;
}

const PractitionerHours: FC<PractitionerHoursProps> = ({
  selectedPractitioner,
}) => {
  const today = moment().format('YYYY-MM-DD');

  const { state, dispatch } = useScheduleNavigator(today);

  const { setIsCollapsed } = useSidebar();

  const { data: clinicSchedule, isLoading: isClinicScheduleLoading } =
    useClinicSchedule();

  const [isOutsideModalOpen, setIsOutsideModalOpen] = useState(false);
  const [isDataChecking, setIsDataChecking] = useState(false);

  const [isShowing, setIsShowing] = useState(false);

  const { setIsPractitionerCollapsed } = usePractitionerSidebar();

  const {
    data: practitionerSettings,
    isLoading: isPractitionerSettingsLoading,
  } = usePractitionerSettings(selectedPractitioner.id);

  const practitionerSchedule = isPractitionerSettingsLoading
    ? null
    : practitionerSettings.isAdvancedSchedule
    ? practitionerSettings.advancedSchedule
    : practitionerSettings.schedule;

  const {
    data: practitionerException,
    isLoading: isPractitionerExceptionLoading,
  } = usePractitionerException({
    practitionerId: selectedPractitioner.id,
    startDate: moment(state.monthValue).startOf('month').format('YYYY-MM-DD'),
    endDate: moment(state.monthValue).endOf('month').format('YYYY-MM-DD'),
  });

  const { data: clinicException, isLoading: isClinicExceptionLoading } =
    useClinicException({
      source: clinicSchedule ? clinicSchedule.currentWorkingHour : '',
      shouldRun: !!clinicSchedule,
      startDate: moment(state.monthValue).startOf('month').format('YYYY-MM-DD'),
      endDate: moment(state.monthValue).endOf('month').format('YYYY-MM-DD'),
    });

  const {
    data: outsidePractitionerHourAppts,
    isLoading: isOutsidePractitionerHourLoading,
    mutate,
  } = useOutsidePractitionerHour(selectedPractitioner.id);

  const isAvailableOutsidePractitionerHourAppts =
    outsidePractitionerHourAppts && outsidePractitionerHourAppts.length > 0;

  const isPageLoading =
    (isClinicScheduleLoading ||
      isPractitionerSettingsLoading ||
      isClinicExceptionLoading ||
      isPractitionerExceptionLoading ||
      isOutsidePractitionerHourLoading) &&
    state.monthValue === today;

  if (isPageLoading) {
    return <LoadingScreen />;
  }

  const onCheckOutsidePractitionerHourAppts = async () => {
    const response = await axiosInstance.get<IAppointmentDetail[]>(
      `/appointments/out-practitioner-hours?doctorId=${selectedPractitioner.id}`
    );

    const newApptIds = response.data.map((item) => item.id);
    const prevApptIds =
      outsidePractitionerHourAppts?.map((item) => item.id) || [];

    const isNewAppts = newApptIds?.some((id) => !prevApptIds.includes(id));

    await mutate(response.data, { revalidate: false });

    if (
      (response.data.length !== 0 &&
        prevApptIds.length !== newApptIds.length) ||
      isNewAppts
    ) {
      setIsOutsideModalOpen(true);
    }
  };

  const handleClickOutsideApptIndicator = async () => {
    setIsDataChecking(true);

    const response = await axiosInstance.get<IAppointmentDetail[]>(
      `/appointments/out-practitioner-hours?doctorId=${selectedPractitioner.id}`
    );

    await mutate(response.data, { revalidate: false });

    setIsDataChecking(false);
    if (response.data.length === 0) {
      return renderToast({
        type: 'info',
        message: 'No patients outside of working hours',
      });
    }

    setIsOutsideModalOpen(true);
  };

  const onCloseDrawer = () => {
    setIsShowing(false);
    setIsCollapsed(false);
    setIsPractitionerCollapsed(false);
  };

  return (
    <>
      {isDataChecking && <LoadingScreen />}
      {isOutsideModalOpen && isAvailableOutsidePractitionerHourAppts && (
        <OutsideOfficeHoursModal
          isOutsidePractitionerMode
          data={outsidePractitionerHourAppts!}
          onClose={() => setIsOutsideModalOpen(false)}
          header={
            <>
              <h3 className="font-bold text-20 leading-[3rem]">
                Patients scheduled outside of practitioner working hours
              </h3>

              <div className="mt-1.6 mb-1.9">
                {selectedPractitioner.title} {selectedPractitioner.name}’s
                schedule was recently updated and we found the following
                patients with future appointments outside of their new working
                hours. Please update the appointments in your practice software.
              </div>
            </>
          }
        />
      )}
      {/* <NavigationBlocker when={isDataChanged} /> */}
      <CommonButton
        variant="secondary"
        className="absolute -translate-y-[100%] right-4 !min-w-[18rem]"
        onClick={() => {
          setIsShowing(true);
          setIsCollapsed(true);
          setIsPractitionerCollapsed(true);
        }}
      >
        Practitioner Settings
      </CommonButton>
      <div className="flex mt-3">
        <div className="flex-1">
          <div className="flex flex-row justify-between items-center relative">
            <MonthViewNavigation
              date={state.monthValue}
              setDate={(date) =>
                dispatch({
                  type: 'monthNavigation',
                  payload: { monthValue: date },
                })
              }
              maxDate={moment()
                .add(NEXT_23_MONTH, 'months')
                .endOf('days')
                .toDate()}
            />
            {isAvailableOutsidePractitionerHourAppts && (
              <OutsideApptIndicator
                numberOfAppts={outsidePractitionerHourAppts!.length}
                onClick={handleClickOutsideApptIndicator}
                size="lg"
              >
                Patient is scheduled outside practitioner's hours
              </OutsideApptIndicator>
            )}
          </div>
          <Calendar
            date={state.monthValue}
            practitionerException={practitionerException!}
            clinicException={clinicException!}
            highlightedDate={state.highlightedDate}
            resetHighlightedDate={() =>
              dispatch({ type: 'resetHighLightedDate' })
            }
            selectedPractitioner={selectedPractitioner}
            onCheckOutsidePractitionerHourAppts={
              onCheckOutsidePractitionerHourAppts
            }
            version={clinicSchedule!.version}
            practitionerSchedule={{
              workingHour: practitionerSchedule!,
            }}
            practitionerSettings={practitionerSettings}
          />
        </div>
        <div
          className={`${
            isShowing ? 'w-42 ml-2' : 'w-0 duration-300 transition-all'
          }`}
        ></div>
        <PractitionerSchedule
          isShowing={isShowing}
          onClose={onCloseDrawer}
          state={state}
          dispatch={dispatch}
          selectedPractitioner={selectedPractitioner}
          onCheckOutsidePractitionerHourAppts={
            onCheckOutsidePractitionerHourAppts
          }
          practitionerSchedule={{
            workingHour: practitionerSchedule!,
          }}
        />
      </div>
    </>
  );
};

export default PractitionerHours;
